import React, { useEffect, useState } from "react";
import { useData } from "@pure-ptr/react";
import { Helmet } from "react-helmet";
import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar,
} from "../../../../partials/content/Portlet";
import { Loadable, DataTable } from "../../../../common";
import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { BarChart } from "../../../../common/widgets/BarChart";
import {
    nonPaginatedSorting,
    sortMonthNamesChronologically,
} from "../../../../util";
import { getClaimAnalysisTrends } from "../../../../store/api/statistics";
import { errorToastify } from "../../../../util/toast";

const useStyles = makeStyles((theme) => ({
    filterFormControl: {
        margin: theme.spacing(1),
        minWidth: 100,
        maxWidth: 150,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 250,
        maxWidth: 300,
    },
}));

function ClaimAnalysisTrends({
    filters,
    clients,
    fields,
    selectedClient,
    actions: { setStatisticsFilter },
}) {
    const classes = useStyles();
    const [tableConfig, setTableConfig] = useState({ data: [], sort: [] });

    const {
        // data: trendsData,
        isPending: rotatingApiKey,
        error,
    } = useData(async () => {
        const trendsData = await getClaimAnalysisTrends(
            filters.frequency,
            filters.clientId || selectedClient
        );
        setTableConfig({ ...tableConfig, data: [...(trendsData || [])] });
        return;
    }, [filters.frequency, filters.clientId]);

    useEffect(() => {
        if (error) {
            setTableConfig({sort:[],data:[]})
            errorToastify(
                error?.payload?.error ||
                    "Failed to fetch Claim Trends, Please contact Support"
            );
        }
    }, [error]);

    const processDataForChart = (data) => {
        const aggregatedData = {};
        data.forEach((item) => {
            const dateKey = item.periodLabel;
            if (!aggregatedData[dateKey]) {
                aggregatedData[dateKey] = {
                    count: 0,
                };
            }
            aggregatedData[dateKey].count += item.count;
        });

        const labels =
            filters?.frequency === "monthly"
                ? Object.keys(sortMonthNamesChronologically(aggregatedData))
                : Object.keys(aggregatedData).sort();
        const totalCounts = labels.map((label) => aggregatedData[label].count);
        const counts = labels.map(
            (label) =>
                aggregatedData[label].count != 0 && aggregatedData[label].count
        );

        return {
            labels: labels,
            datasets: [
                {
                    label: "Claim Analysis Trends",
                    data: totalCounts,
                    backgroundColor: "rgba(54, 162, 235, 0.6)",
                    borderColor: "rgba(54, 162, 235, 1)",
                    borderWidth: 2,
                    stack: "Stack 0",
                    tooltipData: counts,
                },
            ],
        };
    };

    useEffect(() => {
        return () => {
            setStatisticsFilter({
                frequency: "monthly",
                clientId: "",
            });
        };
    }, []);

    const handleFilterChange = (e) => {
        setStatisticsFilter({
            ...filters,
            [e.target.name]: e.target.value,
        });
    };

    const handleSort = (sort) => {
        let data = [...(tableConfig?.data || [])];
        const { sort: tSort, tableData: sortedTableData } = nonPaginatedSorting(
            sort,
            [...data]
        );
        setTableConfig({ sort: tSort, data: sortedTableData });
        return sortedTableData;
    };

    return (
        <>
            <Helmet title="Statistics" />
            <Portlet fit="true">
                <Loadable loading={rotatingApiKey != null}>
                    <PortletHeader
                        title="Client Statistics"
                        toolbar={
                            <PortletHeaderToolbar>
                                <Grid container>
                                    <Grid>
                                        <FormControl
                                            className={
                                                classes.filterFormControl
                                            }
                                        >
                                            <InputLabel>Frequency</InputLabel>
                                            <Select
                                                value={filters.frequency}
                                                onChange={handleFilterChange}
                                                name="frequency"
                                            >
                                                <MenuItem value="daily">
                                                    Daily
                                                </MenuItem>
                                                <MenuItem value="weekly">
                                                    Weekly
                                                </MenuItem>
                                                <MenuItem value="monthly">
                                                    Monthly
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid>
                                        <FormControl
                                            className={
                                                classes.filterFormControl
                                            }
                                        >
                                            <InputLabel>Client</InputLabel>
                                            <Select
                                                value={
                                                    filters.clientId ||
                                                    selectedClient
                                                }
                                                onChange={handleFilterChange}
                                                name="clientId"
                                            >
                                                {clients.map((client) => (
                                                    <MenuItem
                                                        key={client.clientId}
                                                        value={client.clientId}
                                                    >
                                                        {client.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </PortletHeaderToolbar>
                        }
                    />

                    <Grid container justifyContent="center" textAlign="center">
                        <Grid item xs={12} md={6} lg={4}>
                            <Portlet>
                                <PortletHeader
                                    title="Claim Trends"
                                    showTitle={true}
                                    toolbar={<></>}
                                />
                                <PortletBody className="pl-0">
                                    {/* @ts-ignore:next-line  */}
                                    <DataTable
                                        fields={fields}
                                        data={tableConfig?.data || []}
                                        onSort={(sortData) =>
                                            handleSort(sortData)
                                        }
                                        sort={tableConfig?.sort}
                                    />
                                </PortletBody>
                            </Portlet>
                        </Grid>
                    </Grid>
                    <PortletBody fit="true">
                        <Grid container className="p-4">
                            <Grid md={12}>
                                <Portlet fluid="true" fit="true">
                                    <BarChart
                                        height="400px"
                                        options={{
                                            type: "bar",
                                            options: {
                                                title: { display: false },
                                                tooltips: {
                                                    callbacks: {
                                                        title: function (
                                                            tooltipItems
                                                        ) {
                                                            return tooltipItems[0]
                                                                .label;
                                                        },
                                                        label: function (
                                                            tooltipItem
                                                        ) {
                                                            const dataPoint =
                                                                tooltipItem.yLabel; // Count value

                                                            return [
                                                                `Claim Analyzed Count: ${dataPoint}`,
                                                            ];
                                                        },
                                                    },
                                                },
                                                legend: { display: false },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                barRadius: 4,
                                                scales: {
                                                    xAxis: {
                                                        suggestedMin: 0,
                                                        beginAtZero: true,
                                                        min: 0,
                                                    },
                                                    yAxis: {
                                                        suggestedMin: 0,
                                                        beginAtZero: true,
                                                        min: 0,
                                                    },

                                                    y: { suggestedMin: 0 },
                                                    x: { suggestedMin: 0 },

                                                    xAxes: [
                                                        {
                                                            stacked: true,
                                                            beginAtZero: true,
                                                            min: 0,
                                                        },
                                                    ],
                                                    yAxes: [
                                                        {
                                                            ticks: {},

                                                            stacked: true,
                                                            beginAtZero: true,
                                                            min: 0,
                                                        },
                                                    ],
                                                },
                                                layout: {
                                                    padding: {
                                                        left: 0,
                                                        right: 0,
                                                        top: 0,
                                                        bottom: 25,
                                                    },
                                                },
                                            },
                                        }}
                                        data={
                                            tableConfig?.data &&
                                            processDataForChart(
                                                tableConfig?.data
                                            )
                                        }
                                        title="Claim Trends"
                                        desc=""
                                    />
                                </Portlet>
                            </Grid>
                        </Grid>
                    </PortletBody>
                </Loadable>
            </Portlet>
        </>
    );
}

export default ClaimAnalysisTrends;
